import { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { buildLink } from "common/routing";
import { useGetSavedViewsForParcelImportQuery } from "../../../../../generated/graphql";
import { useStatusToasts } from "../../../../../hooks/useStatusToasts";
import ArcGISParcelImportConfigStage from "./ArcGISParcelImportConfigStage";
import { ParcelImportWizardContext } from "./context";
import {
  useGetArcGISMappingFieldOptions,
  useParcelImportConfigDefaultValues,
  useParcelImportFormSubmission,
} from "./hooks";
import ParcelImportWizard from "./ParcelImportWizard";
import { ArcGISImportWizardFormData } from "./types";
import { generateMappingErrorMessage, getAuthenticationKeys } from "./utils";
const ArcGISParcelImportFormManager = () => {
  const history = useHistory();

  const { setFieldOptions, workflow } = useContext(ParcelImportWizardContext);

  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const [fetchDefaultValues] =
    useParcelImportConfigDefaultValues<ArcGISImportWizardFormData>({
      workflow,
    });

  const [fetchMappingFieldOptions, { loading: fieldOptionsAreLoading }] =
    useGetArcGISMappingFieldOptions();

  const [submitForm] = useParcelImportFormSubmission();

  const formMethods = useForm<ArcGISImportWizardFormData>({
    // for cases where we need to retrieve default values from
    // the server, react-hook-form allows "defaultValues" to
    // be an async function that returns a promise. Otherwise
    // everything would be "undefined" as a default value
    defaultValues: fetchDefaultValues,
    // this is set to "onChange" because we want to disable the
    // "Next", or submission button, if the form is invalid
    mode: "onChange",
  });

  // destructuring form methods and form state properties directly
  // acts as a way to "subscribe" to when these properties change
  const {
    getValues,
    formState: {
      isLoading: formIsLoading,
      isValid,
      isSubmitSuccessful,
      isSubmitting,
      dirtyFields,
    },
    handleSubmit,
  } = formMethods;

  const { data: savedViewData, loading: savedViewDataLoading } =
    useGetSavedViewsForParcelImportQuery();

  const loading =
    formIsLoading ||
    fieldOptionsAreLoading ||
    isSubmitting ||
    savedViewDataLoading;
  const enableNextBtn = isValid && !loading && !isSubmitSuccessful;

  const onFirstStageNext = async (changeStage: () => void) => {
    const [url, username, password, arcgisPortal] = getValues([
      "url",
      "username",
      "password",
      "arcgisPortal",
    ]);

    await fetchMappingFieldOptions({
      input: {
        url,
        ...getAuthenticationKeys({ username, password, arcgisPortal }),
      },
      onCompleted: ({ availableFields, parcelModelFields }) => {
        setFieldOptions({ availableFields, parcelModelFields });
        changeStage();
      },
    });
  };

  const onLastStageFinish = async () => {
    await handleSubmit(async formData => {
      await submitForm({
        dirtyFields,
        formData,
        savedViews: savedViewData?.account?.savedViews ?? [],
        onCompleted: () => {
          history.push(buildLink("parcelImports"));
          addSuccessToast("Parcel source successfully updated.");
        },
        onError: mapping => {
          if (mapping) {
            addErrorToast(generateMappingErrorMessage(mapping));
          } else {
            addErrorToast("Parcel source failed to update.");
          }
        },
      });
    })();
  };

  return (
    <FormProvider {...formMethods}>
      <ParcelImportWizard
        firstStageRender={() => <ArcGISParcelImportConfigStage />}
        isStageComplete={() => enableNextBtn}
        loading={loading}
        onFirstStageNext={onFirstStageNext}
        onLastStageFinish={onLastStageFinish}
        skipFirstStage={false}
        subtitle="Import ArcGIS rest service"
      />
    </FormProvider>
  );
};

export default ArcGISParcelImportFormManager;
